import type { StoryId } from './story';

export type StoryChapter = {
  id: StoryId;
  number: string;
  title: string;
  body: string;
  facts: readonly string[];
};

export const chapters: readonly StoryChapter[] = [
  {
    id: 'feed',
    number: '00',
    title: 'A feed made only of nights worth keeping.',
    body: 'Concert memories from friends, friends of friends, and the wider crowd. No filler between the shows.',
    facts: ['Memory posts only', 'Friends · Friends+ · Public'],
  },
  {
    id: 'log',
    number: '01',
    title: 'Log the night while it is still ringing.',
    body: 'Find the show, capture your seat and context, then compare it with another night. Your own history decides the score.',
    facts: ['Pairwise ranking, not star ratings', 'Optional seat and venue context'],
  },
  {
    id: 'remember',
    number: '02',
    title: 'Your concert life becomes somewhere you can return to.',
    body: 'Turn a log into a memory, then revisit it by date, city, venue, artist, or the shape of a whole year.',
    facts: ['Timeline + collection map', 'Public · Friends · Private per memory'],
  },
  {
    id: 'connect',
    number: '03',
    title: 'Find the people who heard the same encore.',
    body: 'See friends who were there, shared artist history, crowd memories, taste overlap, and plans around the next show.',
    facts: ['Friends who saw the artist', '“I was there too” + shared memories'],
  },
  {
    id: 'plan',
    number: '04',
    title: 'Know what is next before it becomes what you missed.',
    body: 'Discover artists, venues, and events; keep tickets together; follow plans; and choose the show alerts that help.',
    facts: ['Artist · event · venue discovery', 'On-sale, reminder, and post-show controls'],
  },
];

export const productFacts = [
  ['3', 'memory audiences', 'Public, friends, or private — chosen per post.'],
  ['4', 'scorecard dimensions', 'Performance, sound, venue, and value. Each is optional.'],
  ['1', 'concert life', 'Timeline, map, artists, venues, tickets, and friends in one history.'],
  ['1', 'local invite-code field', 'It checks only for empty input. Your entry stays available for correction; validation is not active, and the code is not sent or persistently saved.'],
] as const;

export const photoMatchTrust = {
  title: 'Keep photo files off the API.',
  body: 'Photo Match reads metadata on your device. Only timestamps and optional location metadata cross the API; photo files and pixels are not uploaded.',
} as const;

export const faqs = [
  {
    question: 'When can I get Sticket?',
    answer:
      'Sticket is in private testing. The public App Store listing is not live yet, so this page does not pretend there is a download or collect a waitlist.',
  },
  {
    question: 'How does a concert get scored?',
    answer:
      'You compare nights — which one wins, or was it a tie? Sticket turns those choices into a personal score and rank. Optional performance, sound, venue, and value notes add context.',
  },
  {
    question: 'Does Sticket look through my photos?',
    answer:
      'Photo Match reads metadata on your device to suggest possible shows. Only timestamps and optional location metadata cross the API; photo files and pixels are not uploaded. You choose what to attach.',
  },
  {
    question: 'Is my concert history public?',
    answer:
      'Every memory can be public, friends-only, or private. Profile, timeline, collection, and map visibility have their own controls too.',
  },
  {
    question: 'Does Sticket sell tickets or share presale codes?',
    answer:
      'No. Sticket can keep ticket details and link you to an official purchase flow. It does not sell tickets or publish private presale codes.',
  },
  {
    question: 'What does connecting Spotify do?',
    answer:
      'It imports artists you already listen to so Sticket can help with artist follows, discovery, and useful alerts. It is not a Sticket sign-in method, and it can be disconnected.',
  },
] as const;
